import { useState } from "react";
import { ChevronLeft, ChevronRight, Calendar, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const STATUS_DOT = {
  pending: "bg-amber-400", quoted: "bg-fuchsia-400", confirmed: "bg-[#00E5FF]",
  completed: "bg-emerald-400", cancelled: "bg-zinc-600", declined: "bg-[#FF1A1A]",
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0,0,0,0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export function AgendaView({ appts }) {
  const { t, tf } = useLang();
  const [view, setView] = useState("week");
  const [ref, setRef] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(iso(new Date()));

  const byDay = (dateStr) => appts.filter((a) => a.preferred_date === dateStr).sort((x, y) => x.preferred_time.localeCompare(y.preferred_time));
  const dayNames = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  const nav = (dir) => setRef((r) => (view === "week" ? addDays(r, dir * 7) : new Date(r.getFullYear(), r.getMonth() + dir, 1)));

  const Header = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <div className="flex items-center gap-2">
        <button onClick={() => nav(-1)} data-testid="agenda-prev" className="w-9 h-9 flex items-center justify-center border border-white/15 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <button onClick={() => { setRef(new Date()); setSelectedDay(iso(new Date())); }} data-testid="agenda-today" className="px-4 h-9 border border-white/15 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] font-mono-tech text-[11px] uppercase tracking-wider transition-colors">{t("admin.ag_today")}</button>
        <button onClick={() => nav(1)} data-testid="agenda-next" className="w-9 h-9 flex items-center justify-center border border-white/15 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"><ChevronRight className="w-4 h-4" /></button>
        <span className="ml-2 font-display font-700 text-white capitalize">
          {ref.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </span>
      </div>
      <div className="inline-flex border border-white/15">
        {["week", "month"].map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`agenda-view-${v}`} className={`px-4 h-9 font-mono-tech text-[11px] uppercase tracking-wider transition-colors ${view === v ? "bg-[#E60000] text-white" : "text-zinc-400 hover:text-white"}`}>
            {v === "week" ? t("admin.ag_week") : t("admin.ag_month")}
          </button>
        ))}
      </div>
    </div>
  );

  if (view === "week") {
    const ws = startOfWeek(ref);
    const days = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
    return (
      <div data-testid="agenda-week">
        <Header />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-white/10 border border-white/10">
          {days.map((d, i) => {
            const ds = iso(d);
            const items = byDay(ds);
            const isToday = ds === iso(new Date());
            return (
              <div key={ds} className="bg-[#0C0C0E] min-h-[160px] p-2.5">
                <div className={`flex items-baseline justify-between mb-2 pb-2 border-b ${isToday ? "border-[#E60000]" : "border-white/5"}`}>
                  <span className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500">{t(`admin.days.${dayNames[i]}`).slice(0, 3)}</span>
                  <span className={`font-display font-900 text-lg ${isToday ? "text-[#E60000]" : "text-white"}`}>{d.getDate()}</span>
                </div>
                <div className="space-y-1.5">
                  {items.length === 0 && <span className="font-mono-tech text-[10px] text-zinc-700">—</span>}
                  {items.map((a) => (
                    <div key={a.id} className="border-l-2 border-white/10 pl-2 py-1 bg-[#050505]" data-testid={`agenda-appt-${a.id}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[a.status]}`} />
                        <span className="font-mono-tech text-[11px] text-[#00E5FF]">{a.preferred_time}</span>
                      </div>
                      <div className="text-white text-xs font-semibold truncate">{a.name}</div>
                      <div className="text-zinc-500 text-[10px] truncate">{tf(`services.${a.service_id}.label`, a.service_label)}</div>
                      {a.phone && (
                        <a href={`tel:${a.phone.replace(/\s/g, "")}`} data-testid={`agenda-phone-${a.id}`} className="mt-1 inline-flex items-center gap-1 text-[#00E5FF] hover:text-white transition-colors font-mono-tech text-[10px]" onClick={(e) => e.stopPropagation()}>
                          <Phone className="w-3 h-3" /> {a.phone}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // MONTH
  const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const selItems = byDay(selectedDay);
  return (
    <div data-testid="agenda-month">
      <Header />
      <div className="grid grid-cols-7 gap-px bg-white/10 border border-white/10">
        {dayNames.map((dn) => (
          <div key={dn} className="bg-[#0C0C0E] py-2 text-center font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500">{t(`admin.days.${dn}`).slice(0, 3)}</div>
        ))}
        {cells.map((d) => {
          const ds = iso(d);
          const items = byDay(ds);
          const inMonth = d.getMonth() === ref.getMonth();
          const isToday = ds === iso(new Date());
          const isSel = ds === selectedDay;
          return (
            <button key={ds} onClick={() => setSelectedDay(ds)} data-testid={`agenda-day-${ds}`} className={`bg-[#0C0C0E] min-h-[76px] p-2 text-left transition-colors hover:bg-[#121215] ${isSel ? "ring-1 ring-inset ring-[#E60000]" : ""} ${inMonth ? "" : "opacity-40"}`}>
              <span className={`font-display font-700 text-sm ${isToday ? "text-[#E60000]" : "text-white"}`}>{d.getDate()}</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {items.slice(0, 4).map((a) => <span key={a.id} className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[a.status]}`} />)}
                {items.length > 4 && <span className="font-mono-tech text-[8px] text-zinc-500">+{items.length - 4}</span>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-wider text-[#00E5FF] mb-3">
          <Calendar className="w-4 h-4" /> {selectedDay}
        </div>
        {selItems.length === 0 ? (
          <div className="border border-white/10 bg-[#0C0C0E] py-10 text-center text-zinc-500 font-mono-tech text-sm">{t("admin.ag_none")}</div>
        ) : (
          <div className="space-y-px bg-white/10 border border-white/10">
            {selItems.map((a) => (
              <div key={a.id} className="bg-[#0C0C0E] p-3 flex items-center gap-3" data-testid={`agenda-month-appt-${a.id}`}>
                <span className={`w-2 h-2 rounded-full ${STATUS_DOT[a.status]}`} />
                <span className="font-mono-tech text-sm text-[#00E5FF] w-14">{a.preferred_time}</span>
                <span className="text-white text-sm font-semibold">{a.name}</span>
                <span className="text-zinc-500 text-xs">· {tf(`services.${a.service_id}.label`, a.service_label)}</span>
                {a.phone && (
                  <a href={`tel:${a.phone.replace(/\s/g, "")}`} data-testid={`agenda-month-phone-${a.id}`} title={t("admin.call_client")} className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF] font-mono-tech text-[11px] font-700 hover:bg-[#00E5FF] hover:text-black transition-colors">
                    <Phone className="w-3 h-3" /> {a.phone}
                  </a>
                )}
                <span className={`font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 ${a.phone ? "" : "ml-auto"}`}>{t(`status.${a.status}`)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
